import React from 'react';
import { Link } from 'react-router-dom';
import { Code, Briefcase, GraduationCap, Rocket, Mail, Heart, ArrowRight } from 'lucide-react';

const Developer = () => {
  return (
    <div className="animate-fade-in pt-24">
      <main className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {/* Left: Profile Card */}
          <div className="md:col-span-4">
            <div className="bg-surface/70 backdrop-blur-xl rounded-xl p-8 shadow-soft-card border border-white/50 flex flex-col items-center text-center sticky top-[120px]">
              {/* Profile Image */}
              <div className="relative w-48 h-48 mb-6">
                <div className="absolute inset-0 bg-primary-container rounded-full blur-md opacity-50 animate-pulse" />
                <img
                  className="w-full h-full object-cover rounded-full border-4 border-white relative z-10 shadow-soft-card hover:scale-95 transition-transform duration-200"
                  src="https://avatars.githubusercontent.com/u/212453625?v=4"
                  alt="Cinde Cindrajati"
                />
                {/* Washi tape accent */}
                <div className="absolute -top-3 -right-2 w-12 h-4 bg-secondary-container opacity-80 transform rotate-12 z-20" />
              </div>

              <h1 className="text-headline-lg-mobile md:text-headline-lg text-primary mb-1 font-bold">
                Cinde Cindrajati
              </h1>
              <div className="inline-flex items-center gap-2 bg-secondary-container/50 px-4 py-1.5 rounded-full mb-6">
                <Code size={14} className="text-secondary" />
                <span className="text-label-md text-secondary font-bold">Developer</span>
              </div>
              <p className="text-body-md text-on-surface-variant mb-8 font-medium">
                Creating cute, cozy digital spaces where memories can live softly.
              </p>
              <button className="w-full bg-primary-container text-on-primary-container text-label-md py-3 px-6 rounded-full shadow-bubble hover:scale-95 active:scale-90 transition-all duration-200 flex items-center justify-center gap-2 font-bold">
                <Mail size={20} />
                Say Hello
              </button>
            </div>
          </div>

          {/* Right: Bento Content */}
          <div className="md:col-span-8 space-y-8">
            {/* About Section */}
            <div className="bg-white rounded-xl p-8 shadow-soft-card relative overflow-hidden group">
              <div className="absolute -right-10 -top-10 w-32 h-32 bg-secondary-container rounded-full blur-2xl opacity-20 group-hover:opacity-40 transition-opacity" />
              <div className="flex items-center gap-3 mb-6 relative z-10">
                <div className="w-10 h-10 rounded-full bg-surface-container flex items-center justify-center text-primary">
                  <Heart size={20} />
                </div>
                <h2 className="text-headline-md text-on-surface font-bold">About the Developer</h2>
              </div>
              <p className="text-body-lg text-on-surface-variant leading-relaxed relative z-10 font-medium">
                I have a deep passion for building digital experiences that feel warm, approachable, and uniquely human. My goal with projects like <span className="text-primary font-bold">fotoSOP</span> is to step away from cold, utilitarian interfaces and embrace a whimsical, high-tactility aesthetic. I believe software should feel like a safe, cozy space — like soft-serve ice cream or a plush cushion for your memories.
              </p>
            </div>

            {/* Experience & Education Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              {/* Experience */}
              <div className="bg-white rounded-xl p-6 shadow-soft-card hover:scale-[0.98] transition-transform duration-200 border border-transparent hover:border-primary-container cursor-default">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-full bg-surface-container flex items-center justify-center text-secondary">
                    <Briefcase size={14} />
                  </div>
                  <h3 className="text-label-md text-secondary uppercase tracking-wider font-bold">Experience</h3>
                </div>
                <div className="mt-4">
                  <h4 className="text-headline-md text-on-surface font-bold">Founder / CEO</h4>
                  <p className="text-body-md text-primary mt-1 font-medium">PT. ASCIN DIGITAL NUSANTARA</p>
                  <div className="mt-3 flex items-center gap-2">
                    <span className="inline-block w-2 h-2 rounded-full bg-primary-container" />
                    <span className="text-label-sm text-on-surface-variant font-bold">Leading digital innovation</span>
                  </div>
                </div>
              </div>

              {/* Education */}
              <div className="bg-white rounded-xl p-6 shadow-soft-card hover:scale-[0.98] transition-transform duration-200 border border-transparent hover:border-secondary-container cursor-default">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-full bg-surface-container flex items-center justify-center text-secondary">
                    <GraduationCap size={14} />
                  </div>
                  <h3 className="text-label-md text-secondary uppercase tracking-wider font-bold">Education</h3>
                </div>
                <div className="mt-4">
                  <h4 className="text-body-lg text-on-surface font-bold">Kelas XI-TKJ</h4>
                  <p className="text-body-md text-primary mt-1 font-medium">(11 Teknik Jaringan Komputer)</p>
                  <p className="text-label-md text-on-surface-variant mt-3 bg-surface p-2 rounded-lg inline-block font-bold">
                    SMKN 1 BOJONEGORO
                  </p>
                </div>
              </div>
            </div>

            {/* Core Projects */}
            <div className="bg-surface-container-low rounded-xl p-8 shadow-soft-card">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-primary text-on-primary flex items-center justify-center shadow-md">
                  <Rocket size={20} />
                </div>
                <h2 className="text-headline-md text-on-surface font-bold">Core Projects</h2>
              </div>
              <div className="relative w-full h-64 rounded-lg overflow-hidden bg-surface/70 backdrop-blur-xl group cursor-pointer border border-white/50">
                <img
                  className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-500"
                  src="https://lh3.googleusercontent.com/aida-public/AB6AXuDFO0jf4VxObU2mpBeXCIvE9rrYZFMyKSi4Sr7qeQLtqlT9b2GXqZ5MQ4VaWmbn2tL4LAWFpa6pvbDdJUKkzqyeWn9UVWePWwg-xNhH-eA2w9tIeIkwryMMG0D4j5XVuuYm9KI9AkBBUlAuMXou8PqkvLxlvqUw2vJUxeD_KAysCla0WAyDtD3XzxHVDbHoRjTWwtl-Fud1ng8_rQalBns9R1OpOrhRG2bpGkGa5CyWnSlILE5KdV7IpA"
                  alt="PT. ASCIN DIGITAL NUSANTARA workspace"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-surface-dim/90 via-surface-dim/40 to-transparent" />
                <div className="absolute bottom-0 left-0 p-6 w-full">
                  <div className="flex justify-between items-end">
                    <div>
                      <h3 className="text-headline-md text-on-surface mb-1 font-bold">PT. ASCIN DIGITAL NUSANTARA</h3>
                      <p className="text-body-md text-on-surface-variant font-medium">Spearheading whimsical digital transformations.</p>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-white text-primary flex items-center justify-center shadow-lg shadow-bubble">
                      <ArrowRight size={20} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="w-full bg-surface-container-high mt-auto">
        <div className="flex flex-col md:flex-row justify-between items-center px-6 py-8 gap-8 max-w-7xl mx-auto">
          <div className="flex flex-col items-center md:items-start gap-2">
            <div className="text-headline-md text-secondary font-bold">fotoSOP</div>
            <div className="text-body-md text-on-surface-variant text-center md:text-left font-medium">
              © 2024 fotoSOP by Sopia. Made with love and sparkles.
            </div>
          </div>
          <nav className="flex flex-wrap justify-center gap-8">
            <span className="text-body-md text-on-surface-variant hover:text-primary transition-colors opacity-80 hover:opacity-100 font-medium cursor-pointer">Privacy Policy</span>
            <span className="text-body-md text-on-surface-variant hover:text-primary transition-colors opacity-80 hover:opacity-100 font-medium cursor-pointer">Terms of Service</span>
            <span className="text-body-md text-on-surface-variant hover:text-primary transition-colors opacity-80 hover:opacity-100 font-medium cursor-pointer">Contact Support</span>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default Developer;
